import React, { useMemo, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from "./Popover";
import { Command, CommandEmpty, CommandInput, CommandList } from './Command';
import { CommandItem } from 'cmdk';
import { ChevronDownIcon, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ScrollArea } from './ScrollArea';
import { isPathEqual, pathListIncludesPath, typeIconMap } from '@/constants/tags';
import { type TagTree } from '@/app/types/tags';
import { Checkbox } from './CheckBox';
import { ChevronRightIcon } from 'lucide-react';
import { Badge } from "@/components/ui/Badge";
import { Button } from './Button';

type SingleProps = {
  multiple?: false;
  value: number[] | undefined;
  onChange: (value: number[] | undefined) => void;
}
type DiplayTagTreeInput = Omit<TagTree, 'children'> & { depth?: number; pathDisplay?: string; children: TagTree[] }
type DiplayTagTree = Omit<TagTree, 'children'> & { depth: number; pathDisplay: string; children: TagTree[] }
type MultipleProps = {
  multiple: true;
  value: number[][];
  onChange: (value: number[][]) => void;
}

export const PathDropdown = ({
  multiple,
  label,
  value,
  onChange,
  groups,
  placeholder,
  searchPlaceholder,
  noResultsText,
  className,
  placeholderClassname,
  inputClassname,
  listItemClassname,
  onlyLeafServices = true
}: {
  groups: TagTree[];
  label?: string;
  placeholder: string;
  searchPlaceholder: string;
  noResultsText: string;
  className?: string;
  placeholderClassname?: string;
  inputClassname?: string;
  listItemClassname?: string;
  onlyLeafServices?: boolean;
} & (SingleProps | MultipleProps)) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  function flatten(nodes: DiplayTagTreeInput[], depth = 0, parent: DiplayTagTree | null = null) {
    const result: DiplayTagTree[] = []

    for (const node of nodes) {
      const {depth: _depth, ...nodeWithoutDepth} = node;
      const resultNode = { 
        ...nodeWithoutDepth, 
        depth: depth, 
        pathDisplay: parent?.pathDisplay ? `${parent.pathDisplay} > ${node.name}` : node.name
      };
      result.push(resultNode)

      if (node.children && node.children.length > 0 && expanded.has(String(node.tag_id))) {
        result.push(...flatten(node.children, depth + 1, resultNode))
      }
    }

    return result
  }

  const allTags = useMemo(() => {
    const map = new Map<number, TagTree>();

    const walk = (nodes: TagTree[]) => {
      for (const node of nodes) {
        map.set(node.tag_id, node);
        walk(node.children);
      }
    };

    walk(groups);

    return map;
  }, [groups]);

  const findPathDisplay = (path: number[]): string => {
    return path
      .map(tagId => allTags.get(tagId)?.name)
      .filter(Boolean)
      .join(" > ");
  };

  const items = useMemo(() => flatten(groups), [groups, expanded]);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) setSearch("");
  };

  const triggerLabel = useMemo(() => {
    if (!multiple) return value ? findPathDisplay(value) : undefined;
    if (value.length === 0) return;
    return (
      <div className="flex flex-wrap items-center gap-2"> 
        {value.map(tid => (
          <PathBadge>
            <span>{findPathDisplay(tid)}</span>
            <X width={16} className="hover:text-black" />
          </PathBadge>
        ))}
      </div>
    );
  }, [multiple, items, value]);

  return (
    <div className={cn("relative w-full", className)}>
      <div className="flex flex-col gap-2">
        <label className="absolute text-xs font-medium left-2 -top-2 z-10 bg-white px-1 text-gray-600">
          {label}
        </label>
        <Popover open={open} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <div
              className={cn(
                "w-full p-2 border cursor-pointer flex items-center justify-between border border-gray-300",
                inputClassname
              )}
              onClick={() => setOpen(true)}
            >
              <span className={cn(
                (multiple ? value.length > 0 : !!value) ? "text-black" : "text-gray-400", 
                placeholderClassname,
              )}>
                {triggerLabel || placeholder}
              </span>
              <ChevronDownIcon width={20} className="opacity-50" />
            </div>
          </PopoverTrigger>
          <PopoverContent className="w-[--radix-popper-anchor-width] p-0" >
            <Command>
              <CommandInput
                placeholder={searchPlaceholder}
                className="h-9 border-none"
                value={search}
                onValueChange={setSearch}
              />
              <CommandList onWheel={(e) => e.stopPropagation()}>
                <ScrollArea className="h-[300px] w-full" hideScrollToTop>
                  {items.length === 0 && (
                    <CommandEmpty>{noResultsText}</CommandEmpty>
                  )}
                  {items.map(item => { 
                    const Icon = typeIconMap[item.type];

                    return (
                      <CommandItem
                        key={item.tag_id}
                        style={{ paddingLeft: item.depth * 12 }}
                        onSelect={() => {
                          if (onlyLeafServices && item.children && item.children.length > 0) {
                            setExpanded(prev => {
                              const next = new Set(prev)
                              if (next.has(String(item.tag_id))) next.delete(String(item.tag_id))
                              else next.add(String(item.tag_id))
                              return next
                            })
                          } else if (!onlyLeafServices || item.type === 'service') {
                            if (multiple) {
                              onChange(!pathListIncludesPath(item.path, value) 
                                ? [...value, item.path] 
                                : [...value.filter(tid => !isPathEqual(tid,item.path))]
                              )
                            } else {
                              onChange(item.path)
                            }
                          }
                        }}
                        className={"@container flex flex-row justify-between items-center px-4 hover:bg-gray-100 group"}
                      >
                        <div className="flex-1 flex flex-col @[15rem]:flex-row justify-between items-start @[15rem]:items-center py-1 px-2">
                          <div className="flex items-center gap-x-2 ">
                            {(item.children && item.children.length > 0) && (
                              <Button 
                                variant="ghost"
                                className="p-0 h-auto"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setExpanded(prev => {
                                    const next = new Set(prev)
                                    if (next.has(String(item.tag_id))) next.delete(String(item.tag_id))
                                    else next.add(String(item.tag_id))
                                    return next
                                  })
                                }} 
                              >
                              {!expanded.has(String(item.tag_id)) 
                                ? <ChevronRightIcon width={16} />
                                : <ChevronDownIcon width={16} />}
                              </Button>
                            )} 
                            <Icon className="h-4 w-4 text-muted-foreground" /> 
                            <span className={listItemClassname}>{item.name}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-muted-foreground opacity-100 text-[9.5px] @[15rem]:text-xs @[15rem]:opacity-0 group-hover:opacity-100">{item.pathDisplay}</span>
                          </div>
                        </div>
                        <Checkbox 
                          className={cn(
                            onlyLeafServices && (item.type !== 'service' || (item.children && item.children.length > 1)) && "opacity-0"
                          )}
                          checked={multiple ? pathListIncludesPath(item.path, value) : (value && isPathEqual(value, item.path))} 
                        />
                      </CommandItem>
                    )
                  })}
                </ScrollArea>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}

export function PathBadge({ children, className }: { children: React.ReactNode, className?: string }) {
  return (
    <Badge
      variant="secondary"
      className={cn(
        "font-normal text-sm flex items-center gap-2 bg-primary/40 border border-primary py-[2px] px-2 rounded-md hover:shadow-sm self-start", 
        className
      )}
    >
      {children}
    </Badge>
  );
}
